import React, { useState, useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { User, LogOut, Menu, FileText, Info } from 'lucide-react'


interface LayoutUser {
  id: number
  email: string
  name?: string
  username?: string
  role?: 'user' | 'admin' | 'manager'
  role_level?: number
}

interface PublicLayoutProps {
  children: React.ReactNode
  user?: LayoutUser
}

export function PublicLayout({ children, user: initialUser }: PublicLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Use SSR user data - don't refetch on client to avoid hydration mismatch
  // The user prop is passed from server and should be consistent
  const [user] = useState<LayoutUser | undefined>(initialUser)

  // Track scroll position for transparent header effect
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Set initial state
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const displayName = user?.name || user?.username || user?.email?.split('@')[0] || 'User'
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Close mobile menu handler
  const closeMobileMenu = () => setMobileMenuOpen(false)

  // Navigation links array for reuse
  const navLinks = [
    { href: '/blog', label: 'Blog', icon: FileText },
    { href: '/about', label: 'About', icon: Info },
  ]

  return (
    <>
      {/* Meta tags are handled server-side via meta-tags gem */}
      <div className="min-h-screen bg-background w-full max-w-full">
        {/* Header - No SidebarTrigger for public pages */}
        <header className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 border-b' : 'bg-transparent'}`}>
          <div className="flex h-16 items-center justify-between w-full px-4 md:px-8 max-w-full">
            <div className="flex items-center gap-2">
              {/* Logo/Brand */}
              <Link
                href="/"
                className={`flex items-center space-x-2 hover:opacity-80 transition-opacity ${!scrolled ? 'text-foreground' : ''}`}
              >
                <img src="/images/nav-logo-48.webp" alt="MantraVid" className="h-10 w-10" />
              </Link>
              
              {/* Public Navigation Links - Desktop only */}
              <nav className="hidden md:flex items-center gap-8 ml-8">
                <Link 
                  href="/blog" 
                  className={`text-lg font-bold transition-colors hover:text-orange-500 ${scrolled ? 'text-muted-foreground' : 'text-foreground/90'}`}
                >
                  Blog
                </Link>
                <Link 
                  href="/about" 
                  className={`text-lg font-bold transition-colors hover:text-orange-500 ${scrolled ? 'text-muted-foreground' : 'text-foreground/90'}`}
                >
                  About
                </Link>
              </nav>
            </div>

            {/* Right side - Desktop: User menu or Sign In + ThemeToggle */}
            <div className="hidden md:flex items-center gap-3">
              <ThemeToggle />
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{displayName}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard" className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        <span>Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        router.delete('/signout', {
                          onSuccess: () => {
                            router.reload()
                          },
                        })
                      }}
                      className="cursor-pointer"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign Out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Button asChild variant="ghost" size="sm">
                    <Link href="/signin">Sign In</Link>
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile: Hamburger menu button (visible on mobile only) */}
            <div className="flex md:hidden items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="right" className="w-full h-full p-0">
            {/* Accessible title and description for screen readers */}
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SheetDescription className="sr-only">Mobile navigation menu with links to Blog and About pages</SheetDescription>
            <div className="flex flex-col h-full">
              {/* Header row: Sign In or Profile menu */}
              <div className="flex items-center justify-between p-4 border-b">
                {user ? (
                  // Signed in: Show profile dropdown with theme toggle, profile link, and sign out
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2 h-auto p-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">{displayName}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="start" forceMount>
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">{displayName}</p>
                          <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={closeMobileMenu}>
                        <ThemeToggle />
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href="/dashboard" className="cursor-pointer">
                          <User className="mr-2 h-4 w-4" />
                          <span>Dashboard</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          closeMobileMenu()
                          router.delete('/signout', {
                            onSuccess: () => {
                              router.reload()
                            },
                          })
                        }}
                        className="cursor-pointer"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sign Out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  // Not signed in: Show theme toggle + sign in button
                  <div className="flex items-center gap-2">
                    <ThemeToggle onClick={closeMobileMenu} />
                    <Button asChild variant="default" size="sm" onClick={closeMobileMenu}>
                      <Link href="/signin">Sign In</Link>
                    </Button>
                  </div>
                )}
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col p-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                  >
                    <link.icon className="h-5 w-5" />
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main content */}
        <main className="w-full max-w-full pt-16">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t py-8 mt-auto w-full max-w-full overflow-x-hidden">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Building with clarity, sharing with purpose.</span>
                <span className="mx-2">·</span>
                © {new Date().getFullYear()} MantraVid. All rights reserved.
              </div>
              <div className="flex items-center gap-6">
                <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
                  Privacy
                </Link>
                <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
                  Terms
                </Link>
                <Link href="/blog/feed" className="text-sm text-muted-foreground hover:text-foreground">
                  RSS
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
